import { useI18n } from 'i18n-utils'
import { DialogHeader } from '../components/DialogHeader'
import { HocElDialog } from '../hoc-element-components/HocElDialog'
import { useDialog } from '../stores/use-dialog'
import { useProjectTheme } from 'store'
import './EntrustDetail.scss'
import { useEntrustDetail } from '../hooks/use-entrust-detail'
import { DefineComponentProps, safeSecondToMillisecond } from 'utils'
import { PositionItemLayout } from '../layouts/PositionInfo.layout'
import { useBaseInfoStore, useExchangeInfoStore, useLoadMore, UserDoubleContractUnitType, useUserCenterContractOrderStore } from 'store'
import { Tag } from '../components/Tag'
import { ContractServices, OrderDirection, TriggerType } from 'network'
import { ListCell } from '../components/ListCell'
import { Pnl } from '../components/Pnl'
import { dayjs, ElTableColumn } from 'element-plus'
import { TabPane, Tabs } from '../components/Tabs'
import { HocElTable } from '../hoc-element-components/HocElTable'
import { OrderListLayout } from '../layouts/Order.layout'
import { SizedBox } from '../components/SizedBox'
import { FullScreenMinHeight } from '../components/FullScreenMinHeight'
import { EmptyData } from '../components/EmptyData'
import { LoadingComponent } from '../components/LoadingComponent'
import { LoadMore } from '../components/LoadMore'
import { StorageIndexDBEnum } from 'store/src/storage/StorageDB'
import { ArrowRight } from '../icons/ArrowRight'
import { UiUtils } from '../ui-utils/ui-utils'


export type EntrustDetailProps = {
  trustId: string
  entrust: ApiResponse.HistoricalCommission
  onClose?: () => void
}

enum EntrustDetailTab {
  /** 成交记录 */
  Trade = 'trade',
  /** 关联委托 */
  RelatedEntrust = 'relatedEntrust',
}

export const EntrustDetail = defineComponent<EntrustDetailProps>({
  props: DefineComponentProps<EntrustDetailProps>(['trustId', 'entrust', 'onClose']),
  setup(props) {
    const { dialogVisible, openDialog, closeDialog } = useDialog()
    const { t } = useI18n()
    const baseInfoStore = useBaseInfoStore()
    const exchangeStore = useExchangeInfoStore()
    const contractOrderListStore = useUserCenterContractOrderStore()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const activeTab = ref(EntrustDetailTab.Trade)

    const entrustDetail = useEntrustDetail(props.trustId, props.entrust)

    const userDoubleContractUnitType = window.localStorage.getItem(StorageIndexDBEnum.UserDoubleContractUnitType) || UserDoubleContractUnitType.USDT

    const onClose = () => {
      if (!dialogVisible.value) return
      closeDialog()
      props.onClose?.()
    }

    onBeforeMount(() => {
      openDialog()
    })

    const relatedEntrustListMore = useLoadMore({
      fetcher: ContractServices.getHistoricalTrade.bind(ContractServices),
      handleParams: (params, dataSource) => {
        const thId = dataSource?.[dataSource.length - 1]?.thId
        return { ...(params || {}), opType: '1', trustId: props.entrust.trustId.toString(), thId }
      },
    })

    const { status, statusColor } = contractOrderListStore.getHistoricalCommissionStatus(props.entrust)

    let dir = ''

    if (props.entrust.positionType === OrderDirection.Buy) {
      if (props.entrust.origType === 5) {
        dir = '<='
      } else if (props.entrust.origType === 6) {
        dir = '>='
      }
    }

    if (props.entrust.positionType === OrderDirection.Sell) {
      if (props.entrust.origType === 5) {
        dir = '>='
      } else if (props.entrust.origType === 6) {
        dir = '<='
      }
    }

    const openName = computed(() => {
      if (userDoubleContractUnitType === UserDoubleContractUnitType.Coin) {
        return contractOrderListStore.getOpenName(props.entrust.symbolId)
      }
      return 'USDT'
    })

    const handleViewDetail = (item: ApiResponse.HistoricalCommission) => {
      UiUtils.showEntrustDetail({
        trustId: item.trustId.toString(),
        entrust: item,
      })
    }

    return () => (
      <HocElDialog v-model={dialogVisible.value} showClose={false} width={800} placement={'bottom'} class="header-pb-0" onClose={onClose}>
        {{
          header: () => <DialogHeader onClose={onClose}>{t('委托详情')}</DialogHeader>,
          default: () => (
            <div class="entrust-detail-layout">
              <PositionItemLayout style={{ borderBottom: 'none' }}>
                <div class="symbol-coin-info">
                  <img width={32} height={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(props.entrust.symbolId)} alt="coin icon" />
                  <div class="coin-info">
                    <div class="coin-name">
                      {exchangeStore.getContractSymbolName(props.entrust.symbolId)} {t('永续')}
                    </div>
                    <div class="flex info-tags gap-4">
                      <Tag color={props.entrust.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>
                        {t(contractOrderListStore.getOrderType(props.entrust))}
                      </Tag>
                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                        {t(entrustDetail.getEntrustType())}
                      </Tag>
                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                        {props.entrust.marginType === 1 ? t('逐仓') : t('全仓')}
                        {props.entrust.leverage}X
                      </Tag>
                    </div>
                  </div>
                  <div class="status" style={{ color: statusColor }}>
                    {t(status)}
                  </div>
                </div>
              </PositionItemLayout>

              <div class="position-info">
                <ListCell title={t('已实现盈亏')} description={<Pnl value={props.entrust.rlzPnl}>USDT</Pnl>} />
                <ListCell title={t('委托类型')} description={t(entrustDetail.getEntrustType())} />
                <ListCell title={t('委托总量')} description={`${contractOrderListStore.getCommissionQty(props.entrust).formatCommonMoney()} ${openName.value}`} />
                <ListCell title={t('已成交量')} description={`${contractOrderListStore.getDealInterestQty(props.entrust).formatCommonMoney()} ${openName.value}`} />
                {props.entrust.liquidated !== 1 && (
                  <ListCell title={t('委托价格')} description={props.entrust.type === 2 ? t('市价') : `${props.entrust.price.formatCommonMoney()} USDT`} />
                )}
                {(props.entrust.origType === 5 || props.entrust.origType === 6) && (
                  <ListCell
                    title={t('触发价格')}
                    description={`${props.entrust.triggerType === TriggerType.Latest ? t('最新价格') : t('标记价格')} ${dir} ${props.entrust.triggerPrice.formatCommonMoney()}`}
                  />
                )}
                {props.entrust.liquidated !== 1 && <ListCell title={t('成交均价')} description={`${props.entrust.avgPrice.formatCommonMoney()} USDT`} />}
                <ListCell title={t('手续费')} description={`${props.entrust.totalFee.formatCommonMoney()} USDT`} />
                <ListCell title={t('创建时间')} description={dayjs(safeSecondToMillisecond(props.entrust.createTime)).format('YYYY-MM-DD HH:mm:ss')} />
                <ListCell title={t('更新时间')} description={dayjs(safeSecondToMillisecond(props.entrust.updateTime)).format('YYYY-MM-DD HH:mm:ss')} />
                <ListCell title={t('订单号')} description={props.entrust.oid} isCopy />
              </div>
              {props.entrust.liquidated === 1 && (
                <div class="entrust-detail-card-bg card-bg">
                  {t('{name}标记价格到达{price}USDT时,您的仓位保证金率大于或等于100%,强制平仓触发,仓位按照标记价格{price}USDT被强平引擎接管。', {
                    name: exchangeStore.getContractSymbolName(props.entrust.symbolId),
                    price: props.entrust.liquidationPosition.liquidationPrice.formatCommonMoney(),
                  })}
                </div>
              )}
              {props.entrust.liquidated !== 1 && (
                <>
                  <SizedBox height={4} />
                  <Tabs v-model={activeTab.value} tabHeight={42}>
                    <TabPane name={EntrustDetailTab.Trade} label={t('成交记录')}>
                      <OrderListLayout class="trade-list-layout" style={{ padding: 0 }}>
                        <LoadingComponent loading={relatedEntrustListMore.loading.value}>
                          <LoadMore
                            loading={relatedEntrustListMore.loadMoreLoading.value}
                            noMore={!relatedEntrustListMore.hasMore.value}
                            onLoadMore={relatedEntrustListMore.loadMore}
                            isEmpty={relatedEntrustListMore.dataSource.value.length === 0}
                          >
                            <HocElTable data={relatedEntrustListMore.dataSource.value}>
                              {{
                                default: () => {
                                  return (
                                    <>
                                      <ElTableColumn label={t('成交数量') + `(${openName.value})`}>
                                        {{
                                          default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                                            const amount = contractOrderListStore.getDealInterestTrade(row)
                                            if (userDoubleContractUnitType === UserDoubleContractUnitType.Coin) {
                                              return amount?.formatCommonMoney() || '--'
                                            }
                                            return amount?.multiplyStr(row.price).formatCommonMoney() || '--'
                                          },
                                        }}
                                      </ElTableColumn>
                                      <ElTableColumn label={t('成交价')}>
                                        {{
                                          default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                                            return row.price?.formatCommonMoney() || '--'
                                          },
                                        }}
                                      </ElTableColumn>
                                      <ElTableColumn label={t('时间')}>
                                        {{
                                          default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                                            return dayjs(safeSecondToMillisecond(row.createTime)).format('YYYY-MM-DD HH:mm:ss')
                                          },
                                        }}
                                      </ElTableColumn>
                                    </>
                                  )
                                },
                                empty: () => {
                                  return <FullScreenMinHeight emptyAreaHeight={300}>{!relatedEntrustListMore.loading.value && <EmptyData />}</FullScreenMinHeight>
                                },
                              }}
                            </HocElTable>
                          </LoadMore>
                        </LoadingComponent>
                      </OrderListLayout>
                    </TabPane>
                    {(props.entrust.origType === 5 || props.entrust.origType === 6) && (
                      <TabPane name={EntrustDetailTab.RelatedEntrust} label={t('关联委托')}>
                        {entrustDetail.limitOrders.value?.map((item) => {
                          const { status, statusColor } = contractOrderListStore.getHistoricalCommissionStatus(item)
                          return (
                            <div class="sub-position-item">
                              <PositionItemLayout style={{ borderBottom: 'none' }}>
                                <div class="symbol-coin-info cursor-pointer" onClick={() => handleViewDetail(item)}>
                                  <img width={32} height={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(item.symbolId)} alt="coin icon" />
                                  <div class="coin-info">
                                    <div class="coin-name flex align-center gap-row-4">
                                      {exchangeStore.getContractSymbolName(item.symbolId)} {t('永续')}
                                      <ArrowRight />
                                    </div>
                                    <div class="flex info-tags gap-4">
                                      <Tag color={item.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>{t(contractOrderListStore.getOrderType(item))}</Tag>
                                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                                        {t(entrustDetail.getEntrustType(item))}
                                      </Tag>
                                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                                        {item.marginType === 1 ? t('逐仓') : t('全仓')}
                                        {item.leverage}X
                                      </Tag>
                                    </div>
                                  </div>
                                  <div class="status" style={{ color: statusColor }}>
                                    {t(status)}
                                  </div>
                                </div>
                              </PositionItemLayout>
                              <div class="position-info">
                                {/* 已实现盈亏 */}
                                <ListCell title={t('已实现盈亏')} description={<Pnl value={item.rlzPnl}>USDT</Pnl>} />
                                {/* 委托总量 */}
                                <ListCell title={t('委托总量')} description={`${contractOrderListStore.getCommissionQty(item).formatCommonMoney()} ${openName.value}`} />
                                {/* 已成交量 */}
                                <ListCell title={t('已成交量')} description={`${contractOrderListStore.getDealInterestQty(item).formatCommonMoney()} ${openName.value}`} />
                                {/* 成交均价 */}
                                <ListCell title={t('成交均价')} description={`${item.avgPrice.formatCommonMoney()} USDT`} />
                                {/* 创建时间 */}
                                <ListCell title={t('创建时间')} description={dayjs(safeSecondToMillisecond(item.createTime)).format('YYYY-MM-DD HH:mm:ss')} />
                              </div>
                            </div>
                          )
                        })}
                      </TabPane>
                    )}
                  </Tabs>
                </>
              )}
            </div>
          ),
        }}
      </HocElDialog>
    )
  },
})
